/* eslint-disable no-unneeded-ternary */
import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Tooltip, TextField, ToggleButtonGroup, ToggleButton, Button } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AddIcon from '@mui/icons-material/Add';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { isObjectLike, stringForMatch, formatPrimitive, parseInput, subtreeHasMatch, highlightText, getLevelBackground } from '../utils';

const JsonNode = ({
  label,
  labelSuffix = '',
  highlighted = false,
  value,
  path,
  keyPath,
  level = 0,
  defaultOpen,
  searchTerm,
  onEdit,
  appearance = 'page',
  supportsTouch = false,
  showAllDescendants = false,
  onTogglePin,
  pinnedPathSet,
  onAddProperty,
  activeInlineAddPath,
  onInlineAddPathChange,
  priorityMap = {},
  exactMatchOnly = false,
  showAllPropertiesOfMatchingNodes = true,
  openPathSet,
  onOpenChange,
  renderChildren = true,
  onGraphValue,
  onSelectObject,
  selectedObjectPathSet,
  compact = false
}) => {
  const normalizedSearch = (searchTerm || '').trim().toLowerCase();
  const isFiltering = normalizedSearch.length > 0;
  const [localOpen, setLocalOpen] = useState(defaultOpen ?? (level === 0 && !isFiltering));
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(stringForMatch(value));
  const [addStage, setAddStage] = useState(null);
  const [newPropertyName, setNewPropertyName] = useState('');
  const [newPropertyValue, setNewPropertyValue] = useState('');
  const [propertyNameError, setPropertyNameError] = useState(false);
  const [propertyValueError, setPropertyValueError] = useState(false);
  const [propertyMode, setPropertyMode] = useState('primitive');
  const propertyInputRef = useRef(null);
  const valueInputRef = useRef(null);
  const inlineAddRef = useRef(null);

  useEffect(() => {
    if (openPathSet) return;
    if (defaultOpen !== undefined) setLocalOpen(defaultOpen);
    else if (!isFiltering && level === 0) setLocalOpen(true);
  }, [defaultOpen, isFiltering, level, openPathSet]);

  useEffect(() => {
    setEditText(stringForMatch(value));
  }, [value]);

  useEffect(() => {
    if (addStage === 'key') {
      propertyInputRef.current?.focus();
    } else if (addStage === 'value') {
      valueInputRef.current?.focus();
    }
  }, [addStage]);

  const open = openPathSet ? openPathSet.has(path) : localOpen;
  const handleToggle = useCallback(() => {
    const next = !open;
    if (!openPathSet) setLocalOpen(next);
    onOpenChange?.(path, next);
  }, [onOpenChange, open, openPathSet, path]);
  const backgroundColor = appearance === 'hud' ? 'rgba(10, 28, 36, 0.82)' : getLevelBackground(level);
  const borderColor = appearance === 'hud' ? 'rgba(0,255,170,0.12)' : 'transparent';
  const hoverBackground = appearance === 'hud' ? 'rgba(0,255,170,0.08)' : undefined;
  const textColor = highlighted ? '#ff6b6b' : appearance === 'hud' ? '#e8fdfc' : 'inherit';
  const iconColor = highlighted ? '#ff6b6b' : appearance === 'hud' ? '#00ffaa' : 'inherit';
  const labelSuffixSx = {
    color: highlighted ? '#ff8a80' : appearance === 'hud' ? '#79ffd8' : 'text.secondary',
    fontWeight: 600
  };

  const pathKey = keyPath.join('.');
  const isPinned = pinnedPathSet?.has(pathKey);
  const isObjectSelected = selectedObjectPathSet?.has(JSON.stringify(keyPath));
  const pinColor = appearance === 'hud' ? '#00ffaa' : '#1976d2';
  const pinInactiveColor = appearance === 'hud' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.38)';
  const addIconBackground = appearance === 'hud' ? 'rgba(0,255,170,0.16)' : 'rgba(25,118,210,0.12)';
  const addIconColor = appearance === 'hud' ? '#00ffaa' : '#1976d2';
  const labelLower = typeof label === 'string' ? label.toLowerCase() : '';
  const matchesLabel = isFiltering && !!labelLower && (exactMatchOnly ? labelLower === normalizedSearch : labelLower.includes(normalizedSearch));
  const forceShowHere = showAllDescendants || (isFiltering && matchesLabel);
  const parentIsArray = Array.isArray(value);
  const canAddProperty = isObjectLike(value) && !!onAddProperty;
  const inlineLabelSx = { color: textColor, fontWeight: 600, lineHeight: '32px', minWidth: 64, textAlign: 'right', textTransform: 'lowercase' };
  const missingObjectKey = !parentIsArray && !newPropertyName.trim();
  const submitDisabled = missingObjectKey || propertyNameError || propertyValueError;
  const confirmButtonSx =
    appearance === 'hud'
      ? {
          border: '1px solid rgba(0,255,170,0.35)',
          color: '#00ffaa',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          fontWeight: 600,
          '&:hover': { border: '1px solid #00ffaa', backgroundColor: 'rgba(0,255,170,0.08)' },
          '&.Mui-disabled': {
            borderColor: 'rgba(0,255,170,0.25)',
            color: 'rgba(0,255,170,0.45)',
            backgroundColor: 'rgba(0,255,170,0.08)'
          }
        }
      : {};
  const cancelButtonSx =
    appearance === 'hud'
      ? {
          color: '#dc3545',
          backgroundColor: 'rgba(220,53,69,0.08)',
          '&:hover': {
            color: '#ffffff',
            backgroundColor: '#dc3545',
            boxShadow: '0 0 0 1px rgba(220,53,69,0.4)'
          }
        }
      : {
          color: '#616263',
          '&:hover': { color: '#2f3133', backgroundColor: 'rgba(0,0,0,0.04)' }
        };

  const commitEdit = useCallback(() => {
    setEditing(false);
    onEdit?.(keyPath, parseInput(value, editText));
  }, [onEdit, keyPath, value, editText]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setEditText(stringForMatch(value));
  }, [value]);

  const handlePinToggle = useCallback(
    (event) => {
      event.stopPropagation();
      onTogglePin?.({ keyPath, label, pathKey });
    },
    [onTogglePin, keyPath, label, pathKey]
  );

  const startAddProperty = useCallback(
    (event) => {
      event.stopPropagation();
      if (activeInlineAddPath !== pathKey) onInlineAddPathChange?.(pathKey);
      setAddStage('key');
      setNewPropertyName('');
      setNewPropertyValue('');
      setPropertyNameError(false);
      setPropertyValueError(false);
      setPropertyMode('primitive');
    },
    [activeInlineAddPath, pathKey, onInlineAddPathChange]
  );

  const cancelAddProperty = useCallback(
    (notifyParent = true) => {
      setAddStage(null);
      setNewPropertyName('');
      setNewPropertyValue('');
      setPropertyNameError(false);
      setPropertyValueError(false);
      setPropertyMode('primitive');
      if (notifyParent) onInlineAddPathChange?.(null);
    },
    [onInlineAddPathChange]
  );

  const advanceToValueStage = useCallback(() => {
    const trimmed = newPropertyName.trim();
    if (!parentIsArray && !trimmed) {
      setPropertyNameError(true);
      return;
    }
    if (parentIsArray && trimmed && (Number.isNaN(Number(trimmed)) || !Number.isFinite(Number(trimmed)))) {
      setPropertyNameError(true);
      return;
    }
    setPropertyNameError(false);
    setAddStage('value');
  }, [newPropertyName, parentIsArray]);

  const submitNewProperty = useCallback(() => {
    const trimmedName = newPropertyName.trim();
    if (!parentIsArray && !trimmedName) {
      setPropertyNameError(true);
      return;
    }
    let resolvedIndex;
    if (parentIsArray) {
      if (trimmedName) {
        const parsedIndex = Number(trimmedName);
        if (!Number.isInteger(parsedIndex) || parsedIndex < 0) {
          setPropertyNameError(true);
          return;
        }
        resolvedIndex = parsedIndex;
      }
    }
    const trimmedPrimitive = newPropertyValue.trim();
    const primitivePayload = propertyMode === 'primitive' ? (trimmedPrimitive ? trimmedPrimitive : 'null') : undefined;
    onAddProperty?.({
      keyPath,
      propertyName: parentIsArray ? undefined : trimmedName,
      propertyValue: primitivePayload,
      propertyMode,
      targetType: parentIsArray ? 'array' : 'object',
      arrayIndex: parentIsArray ? resolvedIndex : undefined
    });
    cancelAddProperty();
  }, [onAddProperty, keyPath, newPropertyName, newPropertyValue, propertyMode, cancelAddProperty, parentIsArray]);

  useEffect(() => {
    if (addStage && activeInlineAddPath !== undefined && activeInlineAddPath !== null && activeInlineAddPath !== pathKey) {
      cancelAddProperty(false);
    }
  }, [addStage, activeInlineAddPath, pathKey, cancelAddProperty]);

  useEffect(() => {
    if (!addStage) return;
    const handleGlobalMouseDown = (event) => {
      if (!inlineAddRef.current || inlineAddRef.current.contains(event.target)) return;
      if (!newPropertyName.trim()) cancelAddProperty();
    };
    document.addEventListener('mousedown', handleGlobalMouseDown, true);
    return () => document.removeEventListener('mousedown', handleGlobalMouseDown, true);
  }, [addStage, newPropertyName, cancelAddProperty]);

  if (!isObjectLike(value)) {
    const displayed = formatPrimitive(value);
    const raw = stringForMatch(value);
    const rawLower = raw.toLowerCase();
    const displayedLower = displayed.toLowerCase();
    const matchesValue =
      isFiltering && (exactMatchOnly ? rawLower === normalizedSearch || displayedLower === normalizedSearch : rawLower.includes(normalizedSearch) || displayedLower.includes(normalizedSearch));
    if (!forceShowHere && isFiltering && !matchesLabel && !matchesValue) return null;

    return (
      <Box
        key={path}
        sx={{
          pl: compact ? 0.5 : level * 2,
          pr: compact ? 0.25 : 1,
          py: compact ? 0 : 0.5,
          mb: compact ? 0 : 0.5,
          display: 'flex',
          alignItems: 'center',
          minHeight: compact ? 22 : undefined,
          borderRadius: compact ? 0 : 1,
          backgroundColor: compact ? 'transparent' : backgroundColor,
          border: compact ? 'none' : `1px solid ${borderColor}`,
          boxShadow: appearance === 'hud' ? '0 8px 25px rgba(0,0,0,0.35)' : 'none',
          backdropFilter: appearance === 'hud' ? 'blur(4px)' : undefined,
          color: textColor,
          transition: 'background-color 0.2s ease',
          overflow: 'hidden',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          '&:hover': hoverBackground ? { backgroundColor: hoverBackground } : undefined
        }}
        onDoubleClick={() => setEditing(true)}
        onClick={() => {
          if (supportsTouch && !editing) setEditing(true);
        }}>
        {onTogglePin && (
          <IconButton size="small" onClick={handlePinToggle} sx={{ color: isPinned ? pinColor : pinInactiveColor, mr: 0.5 }}>
            {isPinned ? <CheckCircleIcon fontSize="inherit" /> : <RadioButtonUncheckedIcon fontSize="inherit" />}
          </IconButton>
        )}
        <Typography
          variant={compact ? 'caption' : 'body2'}
          component="span"
          sx={{
            m: compact ? 0 : 1,
            color: textColor,
            overflowX: 'hidden',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0,
            '&::after': label
              ? {
                  content: '":"',
                  marginLeft: 0.25,
                  color: textColor,
                  fontWeight: 'bold',
                  whiteSpace: 'pre'
                }
              : undefined
          }}>
          {label ? (
            <>
              {highlightText(label, normalizedSearch)}
              {labelSuffix ? (
                <Box component="span" sx={labelSuffixSx}>
                  {labelSuffix}
                </Box>
              ) : null}
            </>
          ) : null}
        </Typography>

        {editing ? (
          <input
            autoFocus
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            style={{
              fontSize: 14,
              padding: '2px 4px',
              borderRadius: 4,
              border: `1px solid ${appearance === 'hud' ? 'rgba(0,255,170,0.4)' : 'rgba(0,0,0,0.23)'}`,
              minWidth: 80,
              background: appearance === 'hud' ? 'rgba(3, 22, 28, 0.9)' : '#ffffff',
              color: appearance === 'hud' ? '#e8fdfc' : 'inherit'
            }}
          />
        ) : (
          <Typography variant={compact ? 'caption' : 'body2'} component="span" sx={{ cursor: 'text', color: textColor, ml: compact ? 0.5 : 0 }}>
            {highlightText(displayed, normalizedSearch)}
          </Typography>
        )}
        {onGraphValue && (
          <Tooltip title="Add value to graph">
            <IconButton
              size="small"
              aria-label={`Graph ${label || path}`}
              sx={{ ml: 'auto', p: compact ? '2px' : undefined, color: iconColor }}
              onDoubleClick={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                onGraphValue({ keyPath, label, path, value });
              }}>
              <ShowChartIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  const entries = Array.isArray(value)
    ? value.map((item, idx) => [`[${idx}]`, item, idx])
    : (() => {
        const sorted = Object.entries(value).sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base', numeric: true }));
        const priorityList = priorityMap[pathKey] || [];
        if (!priorityList.length) {
          return sorted.map(([k, v]) => [k, v, k]);
        }
        const prioritySet = new Set(priorityList);
        const prioritized = priorityList.map((key) => (Object.prototype.hasOwnProperty.call(value, key) ? [key, value[key], key] : null)).filter(Boolean);
        const remainder = sorted.filter(([k]) => !prioritySet.has(k)).map(([k, v]) => [k, v, k]);
        return [...prioritized, ...remainder];
      })();
  const labelIsIndex = typeof label === 'string' && /^\[\d+\]$/.test(label);
  const labelDetail = Array.isArray(value) && !labelIsIndex ? ` [${value.length}]` : '';

  const annotatedEntries = entries.map(([childLabel, childValue, childKey]) => {
    const childMatchesFilter = isFiltering ? subtreeHasMatch(childLabel, childValue, normalizedSearch, exactMatchOnly) : false;
    return { childLabel, childValue, childKey, childMatchesFilter };
  });

  const hasMatchingDescendant = annotatedEntries.some(({ childMatchesFilter }) => childMatchesFilter);
  const shouldFilterChildren = showAllPropertiesOfMatchingNodes ? isFiltering && !(showAllDescendants || matchesLabel || hasMatchingDescendant) : isFiltering && !showAllDescendants;
  const visibleEntries = shouldFilterChildren ? annotatedEntries.filter(({ childMatchesFilter }) => childMatchesFilter) : annotatedEntries;
  const totalVisible = visibleEntries.length;
  const hasVisibleChildren = totalVisible > 0;
  const shouldRender = forceShowHere || !isFiltering || totalVisible > 0;
  if (!shouldRender) return null;

  const hasHiddenMatches = isFiltering && !open && hasMatchingDescendant && !matchesLabel;

  return (
    <Box
      key={path}
      sx={{
        pl: compact ? 0.5 : level * 2,
        pr: compact ? 0.25 : 1,
        py: compact ? 0 : 0.75,
        mb: compact ? 0 : 0.5,
        borderRadius: compact ? 0 : 1,
        backgroundColor: compact ? 'transparent' : backgroundColor,
        border: compact ? 'none' : `1px solid ${borderColor}`,
        boxShadow: appearance === 'hud' ? '0 8px 25px rgba(0,0,0,0.35)' : 'none',
        backdropFilter: appearance === 'hud' ? 'blur(4px)' : undefined,
        color: textColor,
        transition: 'background-color 0.2s ease',
        overflow: 'hidden',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        '&:hover': hoverBackground ? { backgroundColor: hoverBackground } : undefined
      }}>
      <Box sx={{ minHeight: compact ? 22 : undefined, display: 'flex', alignItems: 'center', cursor: 'pointer', color: textColor, gap: compact ? 0 : 0.5 }} onClick={handleToggle}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
          sx={{ mr: compact ? 0 : 1, p: compact ? '2px' : undefined, color: iconColor, '&:hover': { color: iconColor } }}>
          {open ? <ExpandMoreIcon fontSize="inherit" /> : <ChevronRightIcon fontSize="inherit" />}
        </IconButton>
        {onTogglePin && (
          <IconButton size="small" onClick={handlePinToggle} sx={{ color: isPinned ? pinColor : pinInactiveColor }}>
            {isPinned ? <CheckCircleIcon fontSize="inherit" /> : <RadioButtonUncheckedIcon fontSize="inherit" />}
          </IconButton>
        )}
        <Typography variant={compact ? 'caption' : 'body2'} component="span" sx={{ color: textColor }}>
          {highlightText(`${label}${labelDetail}`, normalizedSearch)}
          {labelSuffix ? (
            <Box component="span" sx={labelSuffixSx}>
              {labelSuffix}
            </Box>
          ) : null}
        </Typography>
        {hasHiddenMatches && (
          <ChevronLeftIcon
            fontSize="inherit"
            sx={{
              fontSize: 16,
              color: '#ff9800',
              opacity: 0.9,
              ml: 0.35
            }}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        {canAddProperty && (
          <Tooltip title="Add property" placement="top" arrow disableInteractive>
            <IconButton
              size="small"
              onClick={startAddProperty}
              sx={{
                ml: 0.5,
                borderRadius: 1,
                bgcolor: addIconBackground,
                color: addIconColor,
                '&:hover': {
                  bgcolor: addIconBackground,
                  opacity: 0.9
                }
              }}>
              <AddIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
        {onSelectObject && (
          <Tooltip title={isObjectSelected ? 'Deselect object' : 'Select object'}>
            <IconButton
              size="small"
              aria-label={`${isObjectSelected ? 'Deselect' : 'Select'} ${label || path}`}
              sx={{ ml: compact ? 0.25 : 0.5, p: compact ? '2px' : undefined, color: isObjectSelected ? '#2e7d32' : iconColor }}
              onClick={(event) => {
                event.stopPropagation();
                onSelectObject({ keyPath, label, path, value });
              }}>
              {isObjectSelected ? <CheckCircleIcon fontSize="inherit" /> : <OpenInNewIcon fontSize="inherit" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {addStage && (
        <Box
          className="lsv-node__inline-add"
          ref={inlineAddRef}
          sx={{
            mt: 0.75,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            border: `1px dashed ${appearance === 'hud' ? 'rgba(0,255,170,0.4)' : 'rgba(0,0,0,0.25)'}`,
            backgroundColor: appearance === 'hud' ? 'rgba(0,10,12,0.35)' : 'rgba(0,0,0,0.02)',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1
          }}
          onClick={(e) => e.stopPropagation()}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Typography variant="caption" sx={inlineLabelSx}>
              {parentIsArray ? 'index:' : 'property:'}
            </Typography>
            <TextField
              inputRef={propertyInputRef}
              value={newPropertyName}
              onChange={(e) => {
                setNewPropertyName(e.target.value);
                if (propertyNameError) setPropertyNameError(false);
              }}
              placeholder={parentIsArray ? 'Optional index' : 'Enter key'}
              size="small"
              autoComplete="off"
              error={propertyNameError}
              helperText=""
              FormHelperTextProps={{ sx: { display: 'none' } }}
              sx={{
                minWidth: 140,
                maxWidth: 220,
                '& .MuiInputBase-input': { color: textColor, py: 0.5, px: 1 },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: appearance === 'hud' ? 'rgba(5, 25, 30, 0.8)' : '#fff',
                  borderRadius: 1,
                  outline: 'none',
                  boxShadow: 'none',
                  '& legend': {
                    display: 'none'
                  },
                  '& fieldset': {
                    borderColor: propertyNameError ? '#ff7043' : appearance === 'hud' ? 'rgba(0,255,170,0.45)' : 'rgba(0,0,0,0.23)'
                  },
                  '&:hover fieldset': {
                    borderColor: propertyNameError ? '#ff7043' : appearance === 'hud' ? 'rgba(0,255,170,0.7)' : 'rgba(0,0,0,0.45)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: propertyNameError ? '#ff7043' : appearance === 'hud' ? '#00ffaa' : '#1976d2',
                    borderWidth: 1
                  }
                },
                '& .MuiFormHelperText-root': { display: 'none' }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  advanceToValueStage();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelAddProperty();
                }
              }}
            />
            {addStage && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={propertyMode}
                onChange={(_, next) => {
                  if (!next) return;
                  setPropertyMode(next);
                  setPropertyValueError(false);
                  if (next === 'primitive') {
                    setNewPropertyValue('');
                    if (addStage === 'value') {
                      requestAnimationFrame(() => valueInputRef.current?.focus());
                    }
                  } else {
                    setNewPropertyValue('');
                  }
                }}
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    width: 80,
                    px: 1.5,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: appearance === 'hud' ? 'rgba(0,255,170,0.3)' : 'rgba(0,0,0,0.25)',
                    color: appearance === 'hud' ? '#d1fffa' : 'inherit'
                  },
                  '& .MuiToggleButton-root.Mui-selected': {
                    borderColor: appearance === 'hud' ? '#00ffaa' : 'rgba(0,0,0,0.45)',
                    backgroundColor: appearance === 'hud' ? 'rgba(0,255,170,0.18)' : 'rgba(0,0,0,0.08)',
                    color: '#ffffff'
                  }
                }}>
                <ToggleButton value="object">Object</ToggleButton>
                <ToggleButton value="array">Array</ToggleButton>
                <ToggleButton value="primitive">Primitive</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
          {addStage && (
            <Box sx={{ mt: 1, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={inlineLabelSx}>
                  value:
                </Typography>
                {propertyMode === 'primitive' ? (
                  <TextField
                    inputRef={valueInputRef}
                    value={newPropertyValue}
                    onChange={(e) => {
                      setNewPropertyValue(e.target.value);
                      if (propertyValueError && e.target.value.trim()) setPropertyValueError(false);
                    }}
                    placeholder="Enter value"
                    size="small"
                    autoComplete="off"
                    error={propertyValueError}
                    helperText=""
                    FormHelperTextProps={{ sx: { display: 'none' } }}
                    sx={{
                      minWidth: 140,
                      maxWidth: 220,
                      '& .MuiInputBase-input': { color: textColor, py: 0.5, px: 1 },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: appearance === 'hud' ? 'rgba(5, 25, 30, 0.8)' : '#fff',
                        borderRadius: 1,
                        outline: 'none',
                        boxShadow: 'none',
                        '& legend': {
                          display: 'none'
                        },
                        '& fieldset': {
                          borderColor: propertyValueError ? '#ff7043' : appearance === 'hud' ? 'rgba(0,255,170,0.45)' : 'rgba(0,0,0,0.23)'
                        },
                        '&:hover fieldset': {
                          borderColor: propertyValueError ? '#ff7043' : appearance === 'hud' ? 'rgba(0,255,170,0.7)' : 'rgba(0,0,0,0.45)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: propertyValueError ? '#ff7043' : appearance === 'hud' ? '#00ffaa' : '#1976d2',
                          borderWidth: 1
                        }
                      },
                      '& .MuiFormHelperText-root': { display: 'none' }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        submitNewProperty();
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelAddProperty();
                      }
                    }}
                  />
                ) : (
                  <Typography variant="caption" sx={{ color: textColor, fontStyle: 'italic' }}>
                    {propertyMode === 'object' ? 'Creates an empty object you can populate immediately.' : 'Creates an empty array you can populate immediately.'}
                  </Typography>
                )}
              </Box>
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" onClick={cancelAddProperty} sx={cancelButtonSx}>
                  Cancel
                </Button>
                <Button size="small" variant="outlined" onClick={submitNewProperty} disabled={submitDisabled} sx={confirmButtonSx}>
                  Add
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {renderChildren && open && (
        <Box sx={{ mt: compact ? 0 : 0.5, ml: compact ? 1.25 : 0, pl: compact ? 0.75 : 0, borderLeft: compact ? '1px dotted rgba(0,0,0,0.22)' : 'none' }}>
          {hasVisibleChildren
            ? visibleEntries.map(({ childLabel, childValue, childKey, childMatchesFilter }) => (
                <JsonNode
                  key={`${path}.${childLabel}`}
                  label={childLabel}
                  value={childValue}
                  path={`${path}.${childLabel}`}
                  keyPath={[...keyPath, childKey]}
                  level={level + 1}
                  defaultOpen={undefined}
                  searchTerm={searchTerm}
                  onEdit={onEdit}
                  appearance={appearance}
                  supportsTouch={supportsTouch}
                  onTogglePin={onTogglePin}
                  pinnedPathSet={pinnedPathSet}
                  onAddProperty={onAddProperty}
                  activeInlineAddPath={activeInlineAddPath}
                  onInlineAddPathChange={onInlineAddPathChange}
                  priorityMap={priorityMap}
                  exactMatchOnly={exactMatchOnly}
                  showAllPropertiesOfMatchingNodes={showAllPropertiesOfMatchingNodes}
                  showAllDescendants={showAllPropertiesOfMatchingNodes ? showAllDescendants || open || childMatchesFilter : childMatchesFilter}
                  onGraphValue={onGraphValue}
                  onSelectObject={onSelectObject}
                  selectedObjectPathSet={selectedObjectPathSet}
                  compact={compact}
                />
              ))
            : !isFiltering && (
                <Typography variant="body2" component="span" sx={{ pl: 2, color: textColor }}>
                  (empty)
                </Typography>
              )}
        </Box>
      )}
    </Box>
  );
};

JsonNode.propTypes = {
  label: PropTypes.string.isRequired,
  labelSuffix: PropTypes.string,
  highlighted: PropTypes.bool,
  value: PropTypes.any,
  path: PropTypes.string.isRequired,
  keyPath: PropTypes.array.isRequired,
  level: PropTypes.number,
  defaultOpen: PropTypes.bool,
  searchTerm: PropTypes.string,
  onEdit: PropTypes.func,
  appearance: PropTypes.oneOf(['page', 'hud']),
  supportsTouch: PropTypes.bool,
  showAllDescendants: PropTypes.bool,
  onTogglePin: PropTypes.func,
  pinnedPathSet: PropTypes.instanceOf(Set),
  onAddProperty: PropTypes.func,
  activeInlineAddPath: PropTypes.string,
  onInlineAddPathChange: PropTypes.func,
  priorityMap: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  exactMatchOnly: PropTypes.bool,
  showAllPropertiesOfMatchingNodes: PropTypes.bool,
  openPathSet: PropTypes.instanceOf(Set),
  onOpenChange: PropTypes.func,
  renderChildren: PropTypes.bool,
  onGraphValue: PropTypes.func,
  onSelectObject: PropTypes.func,
  selectedObjectPathSet: PropTypes.instanceOf(Set),
  compact: PropTypes.bool
};

export default JsonNode;
